import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Establishments";
import {
  setStatusEstablishments,
  getShowEstablishments,
  deleteEstablishmentsData,
} from "@/Api/establishments";
import LogoFavicon from "../../../../public/images/logo-favicon.png";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Foto</TableHead>
            <TableHead>Nome</TableHead>

            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell>
                  <img
                    src={item.image || LogoFavicon}
                    alt={item.name}
                    className="size-7 aspect-square object-cover rounded"
                  />
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>

                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={item?.is_active}
                    params={{
                      func: setStatusEstablishments,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-establishment")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-establishment")}>
                      <DeleteBtn
                        params={{
                          func: deleteEstablishmentsData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-establishment-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Estabelecimento" }}
                        params={{
                          func: getShowEstablishments,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <ScrollArea className="h-[500px] pr-4">
                            <div className="w-full h-auto">
                              <div className="flex flex-col gap-3">
                                <Grid.Item name="Nome" flex={0}>
                                  {responseApi?.name}
                                </Grid.Item>
                                <Grid.Item name="Logo" flex={0}>
                                  <img
                                    src={responseApi?.image || LogoFavicon}
                                    alt={responseApi?.name}
                                    className="w-full aspect-square object-cover rounded"
                                  />
                                </Grid.Item>
                                <Grid.Item name="Status" flex={0}>
                                  {responseApi?.is_active ? "Ativo" : "Inativo"}
                                </Grid.Item>
                                <Grid.Item name="Métodos de Entrega" flex={0}>
                                  <div className="flex flex-wrap gap-2">
                                    {responseApi?.delivery_methods?.map(
                                      (method) => (
                                        <span
                                          key={method.uuid}
                                          className="px-2 py-1 bg-slate-100 rounded text-sm"
                                        >
                                          {method.name}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </Grid.Item>
                                <Grid.Item name="Formas de Pagamento" flex={0}>
                                  <div className="flex flex-wrap gap-2">
                                    {responseApi?.payment_forms?.map((form) => (
                                      <span
                                        key={form.uuid}
                                        className="px-2 py-1 bg-slate-100 rounded text-sm"
                                      >
                                        {form.name}
                                      </span>
                                    ))}
                                  </div>
                                </Grid.Item>
                                <Grid.Item name="Unidades" flex={0}>
                                  <div className="flex flex-wrap gap-2">
                                    {responseApi?.unities?.map((unity) => (
                                      <span
                                        key={unity.uuid}
                                        className="px-2 py-1 bg-slate-100 rounded text-sm"
                                      >
                                        {unity.name}
                                      </span>
                                    ))}
                                  </div>
                                </Grid.Item>
                              </div>
                            </div>
                          </ScrollArea>
                        )}
                        loader={<CreateLoaderInput lines={8} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
